import { HttpErrorResponse } from '@angular/common/http'
import {
	ChangeDetectionStrategy,
	Component,
	EventEmitter,
	Input,
	OnDestroy,
	Output,
	signal,
	WritableSignal,
	inject,
} from '@angular/core'
import { OrderSideEnum } from '@modules/orders-module/enums/order-side.enum'
import { OrderTypeEnum } from '@modules/orders-module/enums/order-type.enum'
import { OrderInterface } from '@modules/orders-module/interfaces/order.interface'
import { TitleOrderTermPipe } from '@modules/orders-module/pipes/title-order-term.pipe'
import { TitleOrderTypePipe } from '@modules/orders-module/pipes/title-order-type.pipe'
import { TranslatePipe, TranslateService } from '@ngx-translate/core'
import { HighlightDirective } from '@shared/directives/higlight.directive'
import { TuiIcon } from '@taiga-ui/core'
import { Subject, takeUntil } from 'rxjs'
import { UseLocales } from '@modules/lang-module/decorators/use-locales'
import ru from './core/locale/ru.json'
import en from './core/locale/en.json'
import { PricePipe } from '@shared/pipes/price.pipe'
import { QuantityPipe } from '@shared/pipes/quantity.pipe'
import { AlertService } from '@shared/services/alert.service'
import { StocksOrdersService } from '@modules/stocks-module/orders/services/stocks-orders.service'
import { StocksOrdersHelperService } from '@modules/stocks-module/orders/services/stocks-orders-helper.service'
import { ButtonComponent } from '@shared/components/ui-kit/button/button.component'

@UseLocales({ ru, en })
@Component({
	selector: 'app-cancel-order',
	templateUrl: './cancel-order.component.html',
	styleUrl: './cancel-order.component.scss',
	imports: [
		TranslatePipe,
		TuiIcon,
		TitleOrderTypePipe,
		HighlightDirective,
		TitleOrderTermPipe,
		PricePipe,
		QuantityPipe,
		ButtonComponent,
	],
	changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CancelOrderComponent implements OnDestroy {
	protected readonly orderSideEnum: typeof OrderSideEnum = OrderSideEnum
	protected readonly orderTypeEnum: typeof OrderTypeEnum = OrderTypeEnum

	@Input() order: OrderInterface | null = null
	@Output() closeDropdown: EventEmitter<void> = new EventEmitter()

	private _destroy$: Subject<void> = new Subject()

	private readonly _ordersStocksService: StocksOrdersService = inject<StocksOrdersService>(StocksOrdersService)
	private readonly _alertService: AlertService = inject<AlertService>(AlertService)
	private readonly _translateService: TranslateService = inject<TranslateService>(TranslateService)
	private readonly _ordersStocksHelperService: StocksOrdersHelperService =
		inject<StocksOrdersHelperService>(StocksOrdersHelperService)

	protected isLoading: WritableSignal<boolean> = signal(false)

	get stopLoss(): OrderInterface | null {
		return this._ordersStocksHelperService.findStopLoss(this.order)
	}

	get takeProfit(): OrderInterface | null {
		return this._ordersStocksHelperService.findTakeProfit(this.order)
	}

	get isStopPriceVisible(): boolean {
		if (!this.order) return false

		return this._ordersStocksHelperService.checkIsStopPriceVisible(this.order.type)
	}

	get isLimitPriceVisible(): boolean {
		if (!this.order) return false

		return this._ordersStocksHelperService.checkIsLimitPriceVisible(this.order.type, this.order.order_class)
	}
	protected cancelOrder(): void {
		if (!this.order) return

		this.isLoading.set(true)

		this._ordersStocksService
			.cancelOrder(this.order.id)
			.pipe(takeUntil(this._destroy$))
			.subscribe({
				next: (): void => {
					this._alertService.success(this._translateService.instant('orders.orderSuccessfullyCanceled'))
					this.isLoading.set(false)
					this.onCloseDropdown()
				},
				error: (error: HttpErrorResponse): void => {
					this._alertService.error(
						error?.error?.error?.message || this._translateService.instant("orders.couldn'tCancelOrder"),
					)
					this.isLoading.set(false)
				},
			})
	}

	protected onCloseDropdown(): void {
		this.closeDropdown.emit()
	}

	ngOnDestroy(): void {
		this._destroy$.next()
		this._destroy$.complete()
	}
}
